import { useAccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { useCanApproveAccessRequest } from '@frameio/access-requests/hooks/useCanApproveAccessRequest';
import { useAccountSubscriptionDetails } from '@frameio/plan-management/plans/hooks/useAccountSubscriptionDetails';
import { getPlanRate } from '@frameio/plan-management/plans/utils/getPlanRate';
import { formatBytes } from '@frameio/utils/formatters';
import TEXT from './generated/TEXT/AccessRequestBody';

TEXT.DEFINE({
  /** When approval will add a new member to the account */
  approveToAddNewMember: 'Adds one new member to your account.',

  /** When plan has no more seats and needs to be upgraded before new members can be added */
  upgradeToAddMoreMembers: 'To add more members, upgrade your plan.',

  /** Tooltip text displaying new member seat rate and trial info if applicable */
  noStorageIncrement: ({
    memberSeatRate,
    isOnTrial,
  }: {
    memberSeatRate: string;
    isOnTrial?: boolean;
  }) =>
    `Adds one new member to your account for ${memberSeatRate} (plus tax)${
      isOnTrial ? ' after free trial period' : ''
    }.`,

  /** Tooltip text displaying new member seat rate, storage increment amount andtrial info if applicable */
  hasStorageIncrement: ({
    memberSeatRate,
    storageIncrement,
    isOnTrial,
  }: {
    memberSeatRate: string;
    storageIncrement?: string;
    isOnTrial?: boolean;
  }) =>
    `Adds one new member and ${storageIncrement} storage to your account for ${memberSeatRate} (plus tax)${
      isOnTrial ? ' after free trial period' : ''
    }.`,

  /** Renewal and cancellation info */
  renewalInfo: ({ renewalPeriod }: { renewalPeriod?: string }) =>
    `Renews ${renewalPeriod}. Cancel anytime.`,

  /** Renewal period for the plan */
  renewalPeriod: {
    /** Monthly billing period */
    MONTHLY: 'monthly',
    /** Annual billing period */
    YEARLY: 'annually',
    /** Semi-annual billing period */
    SEMIANNUALLY: 'semi-annually',
  },
});

export function useBodyData({ id }: { id: string }) {
  const { data } = useAccessRequestNodeFragment({
    from: {
      id,
      __typename: 'AccessRequest',
    },
  });

  const { hasMemberLicense } = data;
  const {
    perUserStorageIncrement,
    planTier,
    subscriptionCost,
    planPeriod,
    isOnTrial,
  } = useAccountSubscriptionDetails({});
  const { canApprove } = useCanApproveAccessRequest({
    hasMemberLicense,
  });

  const memberSeatRate = getPlanRate(planPeriod ?? '', subscriptionCost);

  const showDynamicStorage =
    (planTier === 'PRO' || planTier === 'TEAM') && perUserStorageIncrement > 0;

  const storageIncrement = showDynamicStorage
    ? formatBytes(perUserStorageIncrement)
    : undefined;

  const renewalPeriod =
    TEXT.renewalPeriod[planPeriod as keyof typeof TEXT.renewalPeriod];

  const tooltip = `${
    storageIncrement
      ? TEXT.hasStorageIncrement({
          memberSeatRate,
          storageIncrement,
          isOnTrial,
        })
      : TEXT.noStorageIncrement({
          memberSeatRate,
          isOnTrial,
        })
  } ${renewalPeriod ? TEXT.renewalInfo({ renewalPeriod }) : ''}`;

  const text = {
    approveToAddNewMember: TEXT.approveToAddNewMember,
    upgradeToAddMoreMembers: TEXT.upgradeToAddMoreMembers,
    tooltip,
  };

  return { text, canApprove, hasMemberLicense };
}
